import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Calendar } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

export function Journal() {
  const { t, L } = useLang();
  const [items, setItems] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [cat, setCat] = useState("all");
  useEffect(() => {
    api.get(`/articles${cat !== "all" ? `?category=${cat}` : ""}`).then((r) => setItems(r.data.items));
  }, [cat]);
  useEffect(() => { api.get("/faqs").then((r) => setFaqs(r.data.items)); }, []);

  const cats = ["all", "market", "buying", "selling", "renting"];

  return (
    <div className="pt-24">
      <div className="border-b border-border bg-card/40">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
          <span className="label-caps text-primary">{t("home.journalTag")}</span>
          <h1 className="font-serif text-4xl md:text-6xl font-light mt-3">{t("home.journalTitle")}</h1>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12">
        <div className="flex flex-wrap gap-2 mb-10">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} className={`label-caps px-4 py-2 border transition-colors capitalize ${cat === c ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`} data-testid={`journal-cat-${c}`}>{c}</button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((a) => (
            <Link key={a.id} to={`/journal/${a.slug}`} className="group block" data-testid={`journal-article-${a.slug}`}>
              <div className="aspect-[16/10] hover-zoom mb-4"><img src={a.image} alt="" className="w-full h-full object-cover" /></div>
              <div className="flex items-center gap-3 mb-2">
                <span className="label-caps text-primary">{a.category}</span>
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Calendar size={12} /> {a.published_at}</span>
              </div>
              <h2 className="font-serif text-2xl group-hover:text-primary transition-colors">{L(a.title)}</h2>
              <p className="text-sm text-muted-foreground font-light mt-2 line-clamp-2">{L(a.excerpt)}</p>
            </Link>
          ))}
        </div>

        <section className="mt-24 max-w-3xl mx-auto">
          <h2 className="font-serif text-4xl font-light text-center mb-10">FAQs</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f) => (
              <AccordionItem key={f.id} value={f.id} className="border-border">
                <AccordionTrigger className="font-serif text-xl text-left hover:text-primary" data-testid={`faq-${f.id}`}>{L(f.q)}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-light text-base leading-relaxed">{L(f.a)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </div>
    </div>
  );
}

export function Article() {
  const { slug } = useParams();
  const { t, L } = useLang();
  const [a, setA] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); api.get(`/articles/${slug}`).then((r) => setA(r.data)); }, [slug]);
  if (!a) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-primary" size={32} /></div>;

  return (
    <article className="pt-24">
      <div className="relative h-[55vh] min-h-[380px]">
        <img src={a.image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        <div className="absolute bottom-0 w-full">
          <div className="max-w-3xl mx-auto px-5 pb-10">
            <Link to="/journal" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-primary mb-4" data-testid="article-back"><ArrowLeft size={16} /> {t("home.journalTitle")}</Link>
            <p className="label-caps text-primary">{a.category} · {a.published_at}</p>
            <h1 className="font-serif text-4xl md:text-6xl font-light text-white mt-3">{L(a.title)}</h1>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-5 py-14">
        <p className="font-serif text-2xl font-light leading-relaxed text-muted-foreground mb-8">{L(a.excerpt)}</p>
        <p className="text-lg font-light leading-relaxed whitespace-pre-line">{L(a.content)}</p>
        <p className="mt-10 pt-6 border-t border-border text-sm text-muted-foreground">By {a.author}</p>
      </div>
    </article>
  );
}
